import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createIntegration, updateIntegration } from 'api/integrations';
import * as IntegrationItem from 'assets/js/pages/account-settings/account-integrations/IntegrationItem';
import * as Form from 'components/form';
import { InputCopyButton } from 'components/input';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountIntegrations } from 'customHooks/useCurrentAccountIntegrations';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Integration } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DoubleDonationFormValues {
    publicKey?: string;
    privateKey?: string;
    active: boolean;
}

const DoubleDonation = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data } = useCurrentAccountIntegrations();
    const { doubleDonation } = data || {};
    const hasStoredValues = !!doubleDonation;
    const [isEditing, setIsEditing] = useState(false);
    const firstInputRef = useRef<HTMLInputElement>(null);
    const editButtonRef = useRef<HTMLButtonElement>(null);

    const defaultValues = {
        publicKey: doubleDonation?.authentication.public_key ?? '',
        privateKey: doubleDonation?.authentication.private_key ?? '',
        active: doubleDonation?.active ?? true,
    };

    const methods = Form.useGivebutterForm<DoubleDonationFormValues>({ defaultValues });
    const {
        control,
        formState: { isDirty, isValid },
        handleSubmit,
        path,
        watch,
        reset,
        setValue,
    } = methods;
    const [publicKey, privateKey, active] = watch(['publicKey', 'privateKey', 'active']);

    const isNotEditable = hasStoredValues && !isEditing;

    const { mutate: submit, isPending: isLoading } = useMutation<
        Integration,
        GivebutterAxiosError,
        DoubleDonationFormValues
    >({
        mutationFn: values => {
            const payload = {
                public_key: values.publicKey,
                private_key: values.privateKey,
                active: values.active,
                name: 'doublethedonation',
            };
            return hasStoredValues
                ? updateIntegration(accountId, doubleDonation.id, payload)
                : createIntegration(accountId, payload);
        },
        onSuccess: (response, values) => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.integrations(accountId),
            });
            success('Saved Double the Donation settings!');
            setIsEditing(false);
            reset({
                publicKey: response.authentication.public_key,
                privateKey: response.authentication.private_key,
                active: values.active,
            });
        },
        onError: e => {
            error(e.response?.data?.message || 'Something went wrong. Please check your API keys.');
        },
    });

    return (
        <IntegrationItem.Root>
            <IntegrationItem.Header>
                <IntegrationItem.Logo>
                    <img
                        alt="Double the Donation"
                        className="max-w-[100px]"
                        src={`/images/logos/donation.svg`}
                    />
                </IntegrationItem.Logo>

                <IntegrationItem.Description>
                    <div className="flex-center flex gap-4">
                        <p className="text-xl font-medium">Double the Donation</p>
                        <Tooltip.Root>
                            <Tooltip.Trigger>
                                <FontAwesomeIcon
                                    className="text-gray-600"
                                    icon={faQuestionCircle}
                                />
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content inverse>
                                    <p>
                                        Givebutter integrates with Double the Donation for free, but
                                        a subscription is required to use Double the Donation. Get
                                        started by visiting{' '}
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            href="https://zfrmz.com/40AKXoDGJY1zHyO14a5d"
                                            className="text-white underline hover:text-white"
                                        >
                                            this guide.
                                        </a>
                                    </p>
                                    <Tooltip.Arrow inverse />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    </div>
                    <p className="max-w-xs text-base text-gray-700">
                        Double the Donation is the easiest way to boost your matching gift revenue.
                    </p>

                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://zfrmz.com/40AKXoDGJY1zHyO14a5d"
                    >
                        https://doublethedonation.com
                    </a>
                </IntegrationItem.Description>
                <IntegrationItem.Expander />
            </IntegrationItem.Header>
            <IntegrationItem.Content>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex max-w-sm flex-col items-start gap-4"
                    >
                        <Form.Container>
                            <Form.Label name={path('publicKey')} className="text-base">
                                Public Key
                            </Form.Label>
                            <Form.TextInputRoot
                                name={path('publicKey')}
                                control={control}
                                rules={{ required: !!privateKey && 'Public Key is required' }}
                                inputRootProps={{
                                    disabled: isNotEditable,
                                }}
                            >
                                <Form.TextInputForm
                                    ref={firstInputRef}
                                    placeholder="Enter your Public API key"
                                />
                                {isNotEditable && (
                                    <InputCopyButton
                                        text={doubleDonation.authentication.public_key}
                                    />
                                )}
                            </Form.TextInputRoot>
                            <Form.Error name={path('publicKey')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('privateKey')} className="text-base">
                                Private Key
                            </Form.Label>
                            <Form.TextInputRoot
                                name={path('privateKey')}
                                control={control}
                                rules={{ required: !!publicKey && 'Private Key is required' }}
                                inputRootProps={{
                                    disabled: isNotEditable,
                                }}
                            >
                                <Form.TextInputForm placeholder="Enter your Private API key" />
                                {isNotEditable && (
                                    <InputCopyButton
                                        text={doubleDonation.authentication.private_key}
                                    />
                                )}
                            </Form.TextInputRoot>
                            <Form.Error name={path('privateKey')} />
                        </Form.Container>

                        {isNotEditable ? (
                            <Button
                                variant="solid"
                                ref={editButtonRef}
                                onClick={() => {
                                    flushSync(() => {
                                        setIsEditing(true);
                                    });
                                    firstInputRef.current?.select();
                                }}
                            >
                                Edit
                            </Button>
                        ) : (
                            <div className="flex gap-4">
                                <Form.SubmitButton
                                    isSubmitting={isLoading}
                                    disabled={!isDirty || !isValid}
                                >
                                    Save
                                </Form.SubmitButton>
                                {hasStoredValues ? (
                                    <Button
                                        variant="outline"
                                        color="neutral"
                                        onClick={() => {
                                            flushSync(() => {
                                                setIsEditing(false);
                                            });
                                            editButtonRef.current?.focus();
                                            reset(defaultValues);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                ) : null}
                            </div>
                        )}

                        {hasStoredValues ? (
                            <div className="flex flex-col gap-4">
                                <Form.Container className="flex-row items-center gap-4">
                                    <Form.Label name={path('active')}>
                                        Enable Double the Donation
                                    </Form.Label>
                                    <Form.Switch
                                        control={control}
                                        checked={active}
                                        defaultChecked={defaultValues.active}
                                        name={path('active')}
                                        onCheckedChange={checked => {
                                            setValue(path('active'), checked);
                                            submit({ active: checked });
                                        }}
                                    />
                                </Form.Container>
                                {active ? (
                                    <p className="text-sm">
                                        Double the Donation is enabled on all of your Givebutter
                                        campaigns. Givebutter will automatically send your donor
                                        data to Double the Donation.
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                    </form>
                </Form.Provider>
            </IntegrationItem.Content>
        </IntegrationItem.Root>
    );
};

export { DoubleDonation };
